import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-hot-toast';
import { useSite } from '../../context/SiteContext';
import { getBusinessDateISO } from '../../utils/businessDate';
import { addNovolexLogo } from '../../utils/pdfBranding';
import { drawMonthlyScrapDashboard } from '../../utils/monthlyScrapPdf';

const REPORT_TITLES = {
  work_orders: 'Producción por Work Order',
  perlas: "Perla's Report",
  weekly: 'Resumen semanal de producción',
  scrap: 'Reporte de Scrap',
  monthly_scrap: 'Resumen mensual de Scrap',
  downtime: 'Reporte de tiempos muertos',
  downtime_codes: 'Tiempos por código de tiempo muerto',
  roll_changes: 'Cambios de rollo',
  roll_trace: 'Trazabilidad por rollo y serie',
  items: 'Reporte por Item',
  machines: 'Reporte de desempeño por máquina',
  employees: 'Reporte de empleados',
  audit: 'Reporte de cierre y auditoría',
};

const toNumber = (value) => Number(value || 0);
const fmt = (value) => toNumber(value).toLocaleString('en-US', { maximumFractionDigits: 2 });
const pct = (value) => `${toNumber(value).toFixed(2)}%`;
const list = (value) => (Array.isArray(value) ? value : []);
const uniqueText = (values) => Array.from(new Set(values.filter((value) => String(value ?? '').trim() !== '').map(String))).join(', ');
const reportRows = (data) => list(data?.reports).flatMap((report) => list(report.rows).map((row) => ({ ...row, report })));

const reportMetrics = (report) => {
  const rows = list(report.rows);
  const events = list(report.downtime);
  const production = rows.reduce((total, row) => total + toNumber(row.bags_good), 0);
  const scrap = rows.reduce((total, row) => total + toNumber(row.scrap), 0);
  const counter = rows.reduce((total, row) => total + toNumber(row.counter), 0);
  const runtime = events.filter((event) => String(event.code) === '101').reduce((total, event) => total + toNumber(event.minutes), 0);
  const downtime = events.filter((event) => String(event.code) !== '101').reduce((total, event) => total + toNumber(event.minutes), 0);
  return { production, scrap, counter, runtime, downtime };
};

const groupRows = (rows, keyBuilder, initialBuilder, accumulator) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = keyBuilder(row);
    if (!groups.has(key)) groups.set(key, initialBuilder(row));
    accumulator(groups.get(key), row);
  });
  return Array.from(groups.values());
};

const normalizeWorkOrderIdentifier = (value, fallback = '') => String(value ?? '').trim().toUpperCase() || fallback;
const workOrderWarning = 'WARNING: Las filas naranjas corresponden a Work Orders asociadas a distintos ItemID. Cada Work Order debe tener un solo ItemID.';

const workOrderSections = (data) => {
  const itemsByWorkOrder = new Map();
  reportRows(data).forEach((row) => {
    const workOrder = normalizeWorkOrderIdentifier(row.work_order);
    const item = normalizeWorkOrderIdentifier(row.item);
    if (!workOrder || !item) return;
    if (!itemsByWorkOrder.has(workOrder)) itemsByWorkOrder.set(workOrder, new Set());
    itemsByWorkOrder.get(workOrder).add(item);
  });
  const conflictingWorkOrders = new Set(Array.from(itemsByWorkOrder)
    .filter(([, items]) => items.size > 1).map(([workOrder]) => workOrder));
  if (String(data.work_order || '').trim()) {
    const groups = groupRows(
      reportRows(data),
      ({ report, item }) => JSON.stringify([report.machine?.code, report.date, item]),
      ({ report, item }) => ({
        machine: report.machine?.code || '—', date: report.date || '—', item: item || 'SIN ITEM',
        good: 0, pallets: [], rolls: [], series: [],
      }),
      (group, row) => {
        group.good += toNumber(row.bags_good);
        group.pallets.push(row.pallet_no);
        group.rolls.push(row.roll_no);
        group.series.push(row.paper_series);
      }
    ).sort((a, b) => a.machine.localeCompare(b.machine, undefined, { numeric: true }) || a.date.localeCompare(b.date));
    const items = Array.from(new Set(groups.map((group) => group.item)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return (items.length ? items : ['SIN ITEM']).map((item) => ({
      title: `Producción por Work Order - ${data.work_order} - ${item}`,
      warning: conflictingWorkOrders.has(normalizeWorkOrderIdentifier(data.work_order)) ? workOrderWarning : null,
      warningRows: conflictingWorkOrders.has(normalizeWorkOrderIdentifier(data.work_order))
        ? groups.filter((group) => group.item === item).map((_, index) => index) : [],
      head: ['Máquina', 'Fecha', 'Bolsa Buena', 'Tarimas', 'Rollos', 'Serie de Papel'],
      rows: groups.filter((group) => group.item === item).map((group) => [
        group.machine, group.date, fmt(group.good), uniqueText(group.pallets) || '—',
        uniqueText(group.rolls) || '—', uniqueText(group.series) || '—',
      ]),
    }));
  }
  const groupedRows = reportRows(data).map((row) => ({
    ...row,
    machine: normalizeWorkOrderIdentifier(row.report.machine?.code, '—'),
    workOrder: normalizeWorkOrderIdentifier(row.work_order, 'SIN WO'),
    item: normalizeWorkOrderIdentifier(row.item, 'SIN ITEM'),
  }));
  const groups = groupRows(
    groupedRows,
    ({ machine, workOrder, item }) => JSON.stringify([machine, workOrder, item]),
    ({ machine, workOrder, item }) => ({
      machine, workOrder, item, good: 0,
      pallets: [], rolls: [], series: [],
    }),
    (group, row) => {
      group.good += toNumber(row.bags_good);
      group.pallets.push(row.pallet_no);
      group.rolls.push(row.roll_no);
      group.series.push(row.paper_series);
    }
  ).sort((a, b) => a.machine.localeCompare(b.machine, undefined, { numeric: true })
    || a.workOrder.localeCompare(b.workOrder, undefined, { numeric: true })
    || a.item.localeCompare(b.item, undefined, { numeric: true }));

  return [{
    title: 'Detalle por Work Order',
    warning: conflictingWorkOrders.size ? workOrderWarning : null,
    warningRows: groups.flatMap((group, index) => conflictingWorkOrders.has(group.workOrder) ? [index] : []),
    head: ['Máquina', 'Work Order', 'Item', 'Bolsas buenas', 'Tarimas', 'Rollos', 'Serie de Papel'],
    rows: groups.map((group) => [
      group.machine, group.workOrder, group.item, fmt(group.good),
      uniqueText(group.pallets) || '—', uniqueText(group.rolls) || '—', uniqueText(group.series) || '—',
    ]),
  }];
};

export const getPerlasSections = (data) => {
  // Reuse the Work Order aggregation across dates and shifts, then separate machines.
  const detail = workOrderSections({ ...data, work_order: '' })[0];
  const machines = new Map();
  detail.rows.forEach((row, index) => {
    if (!machines.has(row[0])) machines.set(row[0], { rows: [], warningRows: [] });
    const section = machines.get(row[0]);
    if (detail.warningRows.includes(index)) section.warningRows.push(section.rows.length);
    section.rows.push(row.slice(0, 4));
  });
  return Array.from(machines, ([machine, section]) => ({
    machine, ...section, warning: section.warningRows.length ? detail.warning : null,
  }));
};

const buildPerlasPdf = (data) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  const { margin } = createDocument(REPORT_TITLES.perlas, data, doc);
  doc.setProperties({ title: REPORT_TITLES.perlas, subject: 'Bolsas buenas por máquina, Work Order e ItemID' });
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableWidth = doc.internal.pageSize.getWidth() - margin * 2;
  let y = 112;
  const sections = getPerlasSections(data);
  if (!sections.length) {
    doc.setFontSize(11);
    doc.text('Sin datos para el periodo', margin, y + 12);
  }
  sections.forEach(({ machine, rows, warning, warningRows }) => {
    if (y > pageHeight - 130) {
      doc.addPage();
      y = margin;
    }
    autoTable(doc, {
      startY: y,
      head: [
        [{ content: `Máquina ${machine}`, colSpan: 4, styles: {
          fillColor: [30, 64, 175], textColor: 255, fontSize: 14,
          fontStyle: 'bold', halign: 'left', cellPadding: 10,
        } }],
        ...(warning ? [[{ content: warning, colSpan: 4, styles: {
          fillColor: [255, 237, 213], textColor: [154, 52, 18], fontSize: 9,
          fontStyle: 'normal', halign: 'left', cellPadding: 8,
        } }]] : []),
        ['Máquina', 'Work Order', 'ItemID', 'Bolsa Buena'],
      ],
      body: rows,
      didParseCell: (cell) => {
        if (cell.section === 'body' && warningRows.includes(cell.row.index)) {
          cell.cell.styles.fillColor = [255, 237, 213];
          cell.cell.styles.textColor = [154, 52, 18];
        }
      },
      theme: 'grid',
      margin: { left: margin, right: margin, top: margin, bottom: 40 },
      rowPageBreak: 'avoid',
      headStyles: { fillColor: [219, 234, 254], textColor: [30, 64, 175], fontStyle: 'bold', fontSize: 10, cellPadding: 8 },
      bodyStyles: { fontSize: 10, textColor: [30, 41, 59], cellPadding: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: tableWidth * 0.18, fontStyle: 'bold' },
        1: { cellWidth: tableWidth * 0.34 },
        2: { cellWidth: tableWidth * 0.26 },
        3: { cellWidth: tableWidth * 0.22, halign: 'right' },
      },
    });
    y = doc.lastAutoTable.finalY + 24;
  });
  addPageNumbers(doc);
  return doc;
};

const weeklyModel = (data) => {
  const days = groupRows(
    list(data.reports),
    (report) => report.date,
    (report) => ({ key: report.date, label: report.date, production: 0, scrap: 0, runtime: 0, downtime: 0 }),
    (group, report) => {
      const metrics = reportMetrics(report);
      Object.keys(metrics).forEach((key) => { if (key !== 'counter') group[key] += metrics[key]; });
    }
  ).sort((a, b) => a.key.localeCompare(b.key));

  const machines = groupRows(
    list(data.reports),
    (report) => report.machine?.code || '—',
    (report) => ({ key: report.machine?.code || '—', label: report.machine?.code || '—', production: 0, scrap: 0, runtime: 0, downtime: 0 }),
    (group, report) => {
      const metrics = reportMetrics(report);
      Object.keys(metrics).forEach((key) => { if (key !== 'counter') group[key] += metrics[key]; });
    }
  ).sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));

  return { days, machines };
};

export const getScrapSections = (data) => {
  const rows = reportRows(data).map((row) => ({
    date: row.report.date,
    shift: row.report.shift?.code || '—',
    machine: row.report.machine?.code || '—',
    workOrder: row.work_order || 'SIN WO',
    item: row.item || 'SIN ITEM',
    bagsGood: toNumber(row.bags_good),
    scrap: toNumber(row.scrap),
    counter: toNumber(row.counter),
  })).sort((a, b) => b.scrap - a.scrap);
  const scrapPercentage = (row) => {
    if (row.counter <= 0) return '—';
    const value = (row.scrap / row.counter) * 100;
    return value > 0 && value < 0.005 ? '<0.01%' : pct(value);
  };

  const rankings = (field, title) => ({
    title,
    head: [title.replace('Mayor Scrap por ', ''), 'Bolsas buenas', 'Scrap', '% del contador'],
    rows: groupRows(
      rows,
      (row) => row[field],
      (row) => ({ label: row[field], bagsGood: 0, scrap: 0, counter: 0 }),
      (group, row) => { group.bagsGood += row.bagsGood; group.scrap += row.scrap; group.counter += row.counter; }
    ).filter((group) => group.scrap > 0)
      .sort((a, b) => b.scrap - a.scrap).slice(0, 10)
      .map((group) => [group.label, fmt(group.bagsGood), fmt(group.scrap), scrapPercentage(group)]),
  });

  return [
    rankings('machine', 'Mayor Scrap por Máquina'),
    rankings('workOrder', 'Mayor Scrap por Work Order'),
    rankings('item', 'Mayor Scrap por Item'),
    {
      title: 'Detalle de Scrap',
      head: ['Fecha', 'Turno', 'Máquina', 'Work Order', 'Item', 'Bolsas buenas', 'Scrap', 'Contador', '% Scrap'],
      rows: rows.filter((row) => row.scrap > 0)
        .map((row) => [row.date, row.shift, row.machine, row.workOrder, row.item, fmt(row.bagsGood), fmt(row.scrap), fmt(row.counter), scrapPercentage(row)]),
    },
  ];
};

const downtimeSections = (data) => {
  const events = list(data.reports).flatMap((report) => list(report.downtime)
    .filter((event) => String(event.code) !== '101')
    .map((event) => ({ ...event, report })));
  const groups = groupRows(
    events,
    ({ report, code, work_order: workOrder }) => [report.machine?.code, report.shift?.code, code, workOrder].join('|'),
    ({ report, code, description, work_order: workOrder }) => ({
      code: code || 'SIN CÓDIGO', description: description || '—', machine: report.machine?.code || '—',
      shift: report.shift?.code || '—', workOrder: workOrder || '—', frequency: 0, minutes: 0,
    }),
    (group, event) => { group.frequency += 1; group.minutes += toNumber(event.minutes); }
  ).sort((a, b) => b.minutes - a.minutes);

  return [{
    title: 'Tiempos muertos acumulados',
    head: ['Código', 'Descripción', 'Máquina', 'Turno', 'Work Order', 'Frecuencia', 'Minutos', 'Horas'],
    rows: groups.map((group) => [group.code, group.description, group.machine, group.shift, group.workOrder, fmt(group.frequency), fmt(group.minutes), fmt(group.minutes / 60)]),
  }];
};

export const getDowntimeCodePareto = (data) => {
  const groups = new Map();
  list(data?.reports).forEach((report) => {
    list(report.downtime).forEach((event) => {
      const code = String(event.code ?? '').trim() || 'SIN CÓDIGO';
      if (code === '101') return; // Runtime no es tiempo muerto.
      if (!groups.has(code)) groups.set(code, { code, description: event.description || '—', frequency: 0, minutes: 0 });
      const group = groups.get(code);
      if (group.description === '—' && event.description) group.description = event.description;
      group.frequency += 1;
      group.minutes += toNumber(event.minutes);
    });
  });
  const rows = Array.from(groups.values()).sort((a, b) => b.minutes - a.minutes || a.code.localeCompare(b.code, undefined, { numeric: true }));
  const totalMinutes = rows.reduce((sum, row) => sum + row.minutes, 0);
  let accumulated = 0;
  return {
    totalMinutes,
    totalEvents: rows.reduce((sum, row) => sum + row.frequency, 0),
    rows: rows.map((row) => {
      accumulated += row.minutes;
      return {
        ...row,
        share: totalMinutes ? (row.minutes / totalMinutes) * 100 : 0,
        cumulative: totalMinutes ? (accumulated / totalMinutes) * 100 : 0,
      };
    }),
  };
};

const downtimeCodeSections = (data) => {
  const pareto = getDowntimeCodePareto(data);
  return [{
    title: 'Detalle por código de tiempo muerto',
    note: 'El código 101 (Runtime) no se considera tiempo muerto. El porcentaje acumulado se ordena por minutos de mayor a menor.',
    head: ['Código', 'Descripción', 'Eventos', 'Minutos', 'Horas', '% del total', '% acumulado'],
    rows: [
      ...pareto.rows.map((row) => [row.code, row.description, fmt(row.frequency), fmt(row.minutes), fmt(row.minutes / 60), pct(row.share), pct(row.cumulative)]),
      ...(pareto.rows.length ? [['TOTAL', '', fmt(pareto.totalEvents), fmt(pareto.totalMinutes), fmt(pareto.totalMinutes / 60), '100.00%', '100.00%']] : []),
    ],
  }];
};

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

export const getRollChangeBoxPlots = (data) => {
  const machines = new Map();
  list(data?.reports).forEach((report) => {
    const machine = report.machine?.code || '—';
    list(report.downtime).forEach((event) => {
      if (String(event.code).trim() !== '201') return;
      const minutes = Number(event.minutes);
      if (!Number.isFinite(minutes) || minutes < 0) return;
      if (!machines.has(machine)) machines.set(machine, []);
      machines.get(machine).push(minutes);
    });
  });

  return Array.from(machines, ([machine, values]) => {
    const sorted = values.sort((a, b) => a - b);
    const quantile = (fraction) => {
      const position = (sorted.length - 1) * fraction;
      const lower = Math.floor(position);
      const upper = Math.ceil(position);
      return sorted[lower] + ((sorted[upper] - sorted[lower]) * (position - lower));
    };
    const q1 = quantile(0.25);
    const q3 = quantile(0.75);
    const iqr = q3 - q1;
    const lowFence = q1 - (1.5 * iqr);
    const highFence = q3 + (1.5 * iqr);
    const whiskerLow = sorted.find((value) => value >= lowFence);
    const whiskerHigh = [...sorted].reverse().find((value) => value <= highFence);
    return {
      machine, count: sorted.length, min: sorted[0], q1, median: median(sorted), q3,
      max: sorted[sorted.length - 1], whiskerLow, whiskerHigh,
      outliers: sorted.filter((value) => value < whiskerLow || value > whiskerHigh),
    };
  }).sort((a, b) => a.machine.localeCompare(b.machine, undefined, { numeric: true }));
};

export const getRollChangeSections = (data) => {
  const events = list(data?.reports).flatMap((report) => list(report.downtime)
    .filter((event) => String(event.code).trim() === '201')
    .map((event) => ({
      date: report.date || '—', shift: report.shift?.code || '—', machine: report.machine?.code || '—',
      workOrder: event.work_order || '—', minutes: toNumber(event.minutes), notes: event.notes || '—',
    })));
  const groups = groupRows(
    events,
    (event) => JSON.stringify([event.machine, event.shift]),
    (event) => ({ machine: event.machine, shift: event.shift, minutes: [] }),
    (group, event) => { group.minutes.push(event.minutes); }
  ).map((group) => ({ ...group, total: group.minutes.reduce((sum, minutes) => sum + minutes, 0) }))
    .sort((a, b) => b.total - a.total || a.machine.localeCompare(b.machine, undefined, { numeric: true }) || a.shift.localeCompare(b.shift));

  return [
    {
      title: 'Resumen de cambios de rollo por máquina y turno',
      note: 'Solo eventos A/X con código 201. Los minutos no se atribuyen a un rollo específico porque el evento no captura su identificador.',
      head: ['Máquina', 'Turno', 'Eventos', 'Minutos', 'Horas', 'Promedio min', 'Mediana min', 'Máximo min'],
      rows: groups.map((group) => [group.machine, group.shift, fmt(group.minutes.length), fmt(group.total), fmt(group.total / 60), fmt(group.total / group.minutes.length), fmt(median(group.minutes)), fmt(group.minutes.reduce((highest, minutes) => Math.max(highest, minutes), 0))]),
    },
    {
      title: 'Detalle de eventos 201',
      head: ['Fecha', 'Turno', 'Máquina', 'Work Order', 'Minutos', 'Notas'],
      rows: events.sort((a, b) => a.date.localeCompare(b.date) || a.machine.localeCompare(b.machine, undefined, { numeric: true }) || a.shift.localeCompare(b.shift))
        .map((event) => [event.date, event.shift, event.machine, event.workOrder, fmt(event.minutes), event.notes]),
    },
  ];
};

export const getRollTraceSections = (data) => {
  const entries = reportRows(data).map((row) => ({
    date: row.report.date || '—', shift: row.report.shift?.code || '—', machine: row.report.machine?.code || '—',
    workOrder: String(row.work_order || '').trim() || 'SIN WO',
    roll: String(row.roll_no || '').trim(), series: String(row.paper_series || '').trim(),
    item: row.item || '—', pallet: row.pallet_no || '—', good: toNumber(row.bags_good), scrap: toNumber(row.scrap),
  })).filter((entry) => entry.roll || entry.series);
  const groups = groupRows(
    entries,
    (entry) => JSON.stringify([entry.workOrder, entry.roll, entry.series]),
    (entry) => ({ workOrder: entry.workOrder, roll: entry.roll || 'SIN ROLLO', series: entry.series || 'SIN SERIE', first: entry.date, last: entry.date, machines: new Set(), items: new Set(), good: 0, scrap: 0 }),
    (group, entry) => {
      if (entry.date < group.first) group.first = entry.date;
      if (entry.date > group.last) group.last = entry.date;
      group.machines.add(entry.machine);
      group.items.add(entry.item);
      group.good += entry.good;
      group.scrap += entry.scrap;
    }
  ).sort((a, b) => b.good - a.good || a.workOrder.localeCompare(b.workOrder) || a.roll.localeCompare(b.roll, undefined, { numeric: true }));

  return [
    {
      title: 'Resumen por Work Order, rollo y serie',
      note: 'El número de rollo puede reutilizarse en otra Work Order. Filas sin rollo y sin serie se omiten; identificadores incompletos se muestran como SIN ROLLO o SIN SERIE.',
      head: ['Work Order', 'Rollo', 'Serie de Papel', 'Primera fecha', 'Última fecha', 'Máquinas', 'Items', 'Bolsas buenas', 'Scrap'],
      rows: groups.map((group) => [group.workOrder, group.roll, group.series, group.first, group.last, Array.from(group.machines).join(', '), Array.from(group.items).join(', '), fmt(group.good), fmt(group.scrap)]),
    },
    {
      title: 'Detalle de producción por rollo y serie',
      head: ['Fecha', 'Turno', 'Máquina', 'Work Order', 'Rollo', 'Serie', 'Item', 'Tarima', 'Bolsas buenas', 'Scrap'],
      rows: entries.sort((a, b) => a.date.localeCompare(b.date) || a.machine.localeCompare(b.machine, undefined, { numeric: true }))
        .map((entry) => [entry.date, entry.shift, entry.machine, entry.workOrder, entry.roll || 'SIN ROLLO', entry.series || 'SIN SERIE', entry.item, entry.pallet, fmt(entry.good), fmt(entry.scrap)]),
    },
  ];
};

const itemSections = (data) => {
  const groups = groupRows(
    reportRows(data),
    (row) => row.item || 'SIN ITEM',
    (row) => ({
      item: row.item || 'SIN ITEM', metadata: row.item_metadata || {}, production: 0, scrap: 0,
      machines: new Set(), reportIds: new Set(), runtime: 0,
    }),
    (group, row) => {
      group.production += toNumber(row.bags_good);
      group.scrap += toNumber(row.scrap);
      group.machines.add(row.report.machine?.code || '—');
      if (!group.reportIds.has(row.report.id)) {
        group.reportIds.add(row.report.id);
        group.runtime += reportMetrics(row.report).runtime;
      }
    }
  ).sort((a, b) => b.production - a.production);

  return [{
    title: 'Producción acumulada por Item',
    note: 'El BPM utiliza el tiempo operativo de los reportes en los que aparece cada Item.',
    head: ['Item', 'Descripción', 'Bag Style', 'Bag Size Group', 'Packing Group', 'Producción', 'Scrap', 'BPM', 'Máquinas'],
    rows: groups.map((group) => [
      group.item, group.metadata.name || '—', group.metadata.bag_style || '—', group.metadata.bag_size_group || '—',
      group.metadata.packing_group || '—', fmt(group.production), fmt(group.scrap), fmt(group.runtime > 0 ? group.production / group.runtime : 0),
      Array.from(group.machines).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(', '),
    ]),
  }];
};

const machineSections = (data) => {
  const groups = groupRows(
    list(data.reports),
    (report) => report.machine?.code || '—',
    (report) => ({ machine: report.machine?.code || '—', production: 0, scrap: 0, counter: 0, runtime: 0, downtime: 0 }),
    (group, report) => {
      const metrics = reportMetrics(report);
      Object.keys(metrics).forEach((key) => { group[key] += metrics[key]; });
    }
  ).sort((a, b) => a.machine.localeCompare(b.machine, undefined, { numeric: true }));

  return [{
    title: 'Comparación entre máquinas',
    head: ['Máquina', 'Producción', 'Scrap', 'Tiempo operativo', 'Tiempo muerto', 'BPM', 'Calidad', 'Disponibilidad'],
    rows: groups.map((group) => {
      const qualityDenominator = group.counter > 0 ? group.counter : group.production + group.scrap;
      const totalTime = group.runtime + group.downtime;
      return [
        group.machine, fmt(group.production), fmt(group.scrap), `${fmt(group.runtime)} min`, `${fmt(group.downtime)} min`,
        fmt(group.runtime > 0 ? group.production / group.runtime : 0),
        pct(qualityDenominator > 0 ? (group.production / qualityDenominator) * 100 : 0),
        pct(totalTime > 0 ? (group.runtime / totalTime) * 100 : 0),
      ];
    }),
  }];
};

export const getEmployeeSections = (data) => {
  const roleLabels = { adjuster: 'Ajustador', machinist: 'Maquinista', receiver: 'Recibidor', group_leader: 'Jefe de grupo' };
  const entries = list(data.reports).flatMap((report) => Object.entries(roleLabels).map(([role, roleLabel]) => ({
    report, role, roleLabel, employee: report.employees?.[role], metrics: reportMetrics(report),
  })).filter((entry) => entry.employee));
  const groups = groupRows(
    entries,
    ({ role, employee }) => `${role}|${employee.id ?? employee.employee_number}`,
    ({ roleLabel, employee }) => ({ role: roleLabel, number: employee.employee_number || '—', name: employee.name || '—', reports: new Set(), production: 0, scrap: 0 }),
    (group, entry) => {
      if (group.reports.has(entry.report.id)) return;
      group.reports.add(entry.report.id);
      group.production += entry.metrics.production;
      group.scrap += entry.metrics.scrap;
    }
  ).sort((a, b) => {
    const aTotal = a.production + a.scrap;
    const bTotal = b.production + b.scrap;
    const aEfficiency = aTotal > 0 ? a.production / aTotal : -1;
    const bEfficiency = bTotal > 0 ? b.production / bTotal : -1;
    return b.production - a.production
      || bEfficiency - aEfficiency
      || a.name.localeCompare(b.name)
      || a.role.localeCompare(b.role)
      || String(a.number).localeCompare(String(b.number), undefined, { numeric: true });
  });

  return [{
    title: 'Producción asociada al personal',
    note: 'Eficiencia = producción buena / (producción buena + Scrap) x 100. Estos datos muestran asociación operativa, no una evaluación individual; también influyen la máquina, el material y las condiciones del turno.',
    head: ['Puesto', '# Empleado', 'Nombre', 'Turnos trabajados', 'Producción asociada', 'Scrap asociado', 'Eficiencia %'],
    rows: groups.map((group) => {
      const total = group.production + group.scrap;
      return [group.role, group.number, group.name, fmt(group.reports.size), fmt(group.production), fmt(group.scrap), total > 0 ? pct((group.production / total) * 100) : '—'];
    }),
  }];
};

const auditSections = (data) => [{
  title: 'Cierres, reaperturas y cambios de estado registrados',
  note: 'La bitácora disponible registra cierres y reaperturas; no representa un historial campo por campo.',
  head: ['Fecha', 'Máquina', 'Turno', 'Estado', 'Creado por', 'Cerrado por', 'Fecha de cierre', 'Reaperturas', 'Cambios registrados / Motivos'],
  rows: list(data.reports).map((report) => {
    const changes = list(report.history).map((history) => `${history.from_status} → ${history.to_status} (${history.changed_by || '—'}): ${history.reason || 'Sin motivo'}`);
    return [
      report.date, report.machine?.code || '—', report.shift?.code || '—', report.status,
      report.created_by || '—', report.closed_by || '—', report.closed_at ? new Date(report.closed_at).toLocaleString('es-MX') : '—',
      fmt(list(report.history).filter((history) => history.to_status === 'open').length), changes.join(' | ') || 'Sin cambios registrados',
    ];
  }),
}];

const sectionsFor = (type, data) => {
  if (type === 'work_orders') return workOrderSections(data);
  if (type === 'scrap') return getScrapSections(data);
  if (type === 'downtime') return downtimeSections(data);
  if (type === 'downtime_codes') return downtimeCodeSections(data);
  if (type === 'roll_changes') return getRollChangeSections(data);
  if (type === 'roll_trace') return getRollTraceSections(data);
  if (type === 'items') return itemSections(data);
  if (type === 'machines') return machineSections(data);
  if (type === 'employees') return getEmployeeSections(data);
  if (type === 'audit') return auditSections(data);
  return [];
};

const createDocument = (title, data, existingDoc) => {
  const doc = existingDoc || new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  const margin = 36;
  if (!existingDoc) doc.setProperties({ title, subject: 'Reporte PDF de Conversión' });
  addNovolexLogo(doc, { x: margin, y: 14, width: 82 });
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(30, 64, 175);
  doc.text(title, margin + 100, 42);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(51, 65, 85);
  doc.text(`${data.site?.code || ''}${data.site?.name ? ` - ${data.site.name}` : ''}`, margin, 60);
  doc.text(data.all_history ? 'Periodo: Todo el histórico de la Work Order' : `Periodo: ${data.start_date} al ${data.end_date}`, margin, 76);
  doc.text(data.include_open ? 'Incluye reportes abiertos y cerrados' : 'Solo reportes cerrados', margin, 92);
  return { doc, y: 112, margin };
};

const addTableSection = (doc, section, y, margin) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - 130) {
    doc.addPage();
    y = margin;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 64, 175);
  doc.text(section.title, margin, y);
  y += 10;
  if (section.note || section.warning) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...(section.warning ? [194, 65, 12] : [71, 85, 105]));
    const noteLines = doc.splitTextToSize(section.warning || section.note, doc.internal.pageSize.getWidth() - (margin * 2));
    doc.text(noteLines, margin, y + 8);
    y += (noteLines.length * 11) + 8;
  }
  const rows = section.rows.length > 0 ? section.rows : [[`Sin datos para el periodo`, ...section.head.slice(1).map(() => '—')]];
  autoTable(doc, {
    startY: y,
    head: [section.head],
    body: rows,
    theme: 'grid',
    margin: { left: margin, right: margin, top: margin },
    headStyles: { fillColor: [30, 64, 175], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 },
    bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didParseCell: (cell) => {
      if (cell.section === 'body' && section.warningRows?.includes(cell.row.index)) {
        cell.cell.styles.fillColor = [255, 237, 213];
        cell.cell.styles.textColor = [154, 52, 18];
      }
    },
  });
  return (doc.lastAutoTable?.finalY || y) + 22;
};

const drawBarChart = (doc, title, values, y, margin) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y > pageHeight - 190) {
    doc.addPage();
    y = margin;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 64, 175);
  doc.text(title, margin, y);
  const chartTop = y + 18;
  const chartHeight = 110;
  const chartWidth = pageWidth - (margin * 2);
  const max = Math.max(...values.map((entry) => entry.value), 1);
  const slot = chartWidth / Math.max(values.length, 1);
  values.forEach((entry, index) => {
    const barHeight = (entry.value / max) * 82;
    const barWidth = Math.min(42, slot * 0.58);
    const x = margin + (slot * index) + ((slot - barWidth) / 2);
    const baseY = chartTop + 86;
    doc.setFillColor(37, 99, 235);
    doc.rect(x, baseY - barHeight, barWidth, barHeight, 'F');
    doc.setFontSize(8);
    doc.setTextColor(51, 65, 85);
    doc.text(fmt(entry.value), x + (barWidth / 2), baseY - barHeight - 4, { align: 'center' });
    doc.text(String(entry.label).slice(0, 14), x + (barWidth / 2), baseY + 12, { align: 'center' });
  });
  return chartTop + chartHeight + 12;
};

const drawDowntimeCodePareto = (doc, data, y, margin) => {
  const { rows, totalMinutes } = getDowntimeCodePareto(data);
  const pageWidth = doc.internal.pageSize.getWidth();
  const chartBottom = y + 238;
  const chartTop = y + 39;
  const chartHeight = chartBottom - chartTop;
  const left = margin + 42;
  const right = pageWidth - margin - 48;
  const chartWidth = right - left;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(30, 64, 175);
  doc.text('Pareto de tiempo muerto por código', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(`Total: ${fmt(totalMinutes)} min (${fmt(totalMinutes / 60)} h) · barras: minutos · línea: % acumulado`, margin, y + 15);
  if (!rows.length || totalMinutes <= 0) {
    doc.text('Sin tiempo muerto registrado en el periodo.', margin, y + 55);
    return y + 85;
  }

  // Se muestran los primeros doce códigos y, si aplica, el resto agregado.
  const visible = rows.slice(0, 12).map((row) => ({ ...row }));
  if (rows.length > 12) {
    const remaining = rows.slice(12);
    visible.push({
      code: 'Otros',
      minutes: remaining.reduce((sum, row) => sum + row.minutes, 0),
      cumulative: 100,
    });
  }
  const maxMinutes = Math.max(...visible.map((row) => row.minutes), 1);
  const slot = chartWidth / visible.length;
  const barWidth = Math.min(46, slot * 0.54);

  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.7);
  doc.line(left, chartTop, left, chartBottom);
  doc.line(left, chartBottom, right, chartBottom);
  [0, 0.5, 1].forEach((fraction) => {
    const tickY = chartBottom - fraction * chartHeight;
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(fmt(maxMinutes * fraction), left - 7, tickY + 3, { align: 'right' });
    doc.text(`${Math.round(fraction * 100)}%`, right + 8, tickY + 3);
  });
  doc.setDrawColor(245, 158, 11);
  doc.setLineDashPattern([4, 3], 0);
  const eightyY = chartBottom - chartHeight * 0.8;
  doc.line(left, eightyY, right, eightyY);
  doc.setLineDashPattern([], 0);
  doc.text('80%', right + 8, eightyY + 3);

  let previous = null;
  visible.forEach((row, index) => {
    const centerX = left + slot * (index + 0.5);
    const barHeight = (row.minutes / maxMinutes) * chartHeight;
    doc.setFillColor(37, 99, 235);
    doc.rect(centerX - barWidth / 2, chartBottom - barHeight, barWidth, barHeight, 'F');
    doc.setFontSize(8);
    doc.setTextColor(30, 41, 59);
    doc.text(row.code, centerX, chartBottom + 13, { align: 'center' });
    doc.text(fmt(row.minutes), centerX, chartBottom - barHeight - 5, { align: 'center' });

    const point = [centerX, chartBottom - chartHeight * row.cumulative / 100];
    if (previous) {
      doc.setDrawColor(234, 88, 12);
      doc.setLineWidth(2);
      doc.line(previous[0], previous[1], point[0], point[1]);
    }
    doc.setFillColor(234, 88, 12);
    doc.circle(point[0], point[1], 3, 'F');
    previous = point;
  });
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  if (rows.length > 12) doc.text('Otros agrupa los códigos restantes; el detalle completo aparece en la tabla.', margin, chartBottom + 30);
  return chartBottom + 48;
};

const drawRollChangeBoxPlots = (doc, data, y, margin) => {
  const plots = getRollChangeBoxPlots(data);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = margin + 95;
  const right = pageWidth - margin - 72;
  const scaleMax = Math.max(1, ...plots.map((plot) => plot.max));
  const scale = (minutes) => left + ((minutes / scaleMax) * (right - left));
  const rowHeight = 32;
  let index = 0;

  do {
    if (y > pageHeight - 120) { doc.addPage(); y = margin; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(30, 64, 175);
    doc.text(index ? 'Duración de cambios de rollo por máquina (continuación)' : 'Duración de cambios de rollo por máquina', margin, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text('Eventos 201, turnos agrupados · caja: P25-P75 · línea: mediana · bigotes: 1.5 × RIQ · puntos: atípicos', margin, y + 16);
    if (!plots.length) {
      doc.text('Sin cambios de rollo registrados en el periodo.', margin, y + 43);
      return y + 68;
    }

    const top = y + 34;
    const capacity = Math.max(1, Math.floor((pageHeight - top - margin - 36) / rowHeight));
    const visible = plots.slice(index, index + capacity);
    const bottom = top + (visible.length * rowHeight);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    [0, 0.25, 0.5, 0.75, 1].forEach((fraction) => {
      const x = left + (fraction * (right - left));
      doc.line(x, top, x, bottom);
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(fmt(scaleMax * fraction), x, bottom + 14, { align: 'center' });
    });
    doc.text('Minutos por evento', (left + right) / 2, bottom + 29, { align: 'center' });

    visible.forEach((plot, row) => {
      const centerY = top + ((row + 0.5) * rowHeight);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      doc.text(String(plot.machine).slice(0, 14), left - 10, centerY + 3, { align: 'right' });
      doc.setDrawColor(30, 64, 175);
      doc.setLineWidth(1.2);
      doc.line(scale(plot.whiskerLow), centerY, scale(plot.whiskerHigh), centerY);
      [plot.whiskerLow, plot.whiskerHigh].forEach((value) => doc.line(scale(value), centerY - 5, scale(value), centerY + 5));
      doc.setFillColor(191, 219, 254);
      doc.rect(scale(plot.q1), centerY - 8, Math.max(scale(plot.q3) - scale(plot.q1), 1.5), 16, 'FD');
      doc.setLineWidth(2);
      doc.line(scale(plot.median), centerY - 9, scale(plot.median), centerY + 9);
      doc.setFillColor(234, 88, 12);
      plot.outliers.forEach((value) => doc.circle(scale(value), centerY, 2.6, 'F'));
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105);
      doc.text(`n=${plot.count}  Med. ${fmt(plot.median)}`, right + 8, centerY + 3);
    });
    index += visible.length;
    y = bottom + 48;
    if (index < plots.length) { doc.addPage(); y = margin; }
  } while (index < plots.length);
  return y;
};

const addPageNumbers = (doc) => {
  const pageCount = doc.getNumberOfPages();
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`Página ${page} de ${pageCount}`, width - 36, height - 18, { align: 'right' });
  }
};

const filterWorkOrderData = (data, workOrder) => ({
  ...data,
  work_order: workOrder,
  reports: list(data.reports).map((report) => ({
    ...report,
    rows: list(report.rows).filter((row) => String(row.work_order || '').trim().toUpperCase() === workOrder.toUpperCase()),
  })).filter((report) => report.rows.length > 0),
});

export const buildAdditionalConversionPdf = (type, sourceData) => {
  const workOrder = ['work_orders', 'perlas'].includes(type) ? String(sourceData.work_order || '').trim() : '';
  const data = workOrder ? filterWorkOrderData(sourceData, workOrder) : sourceData;
  if (type === 'perlas') return buildPerlasPdf(data);
  const filteredSections = workOrder ? workOrderSections(data) : null;
  const { doc, margin } = createDocument(filteredSections ? filteredSections[0].title : REPORT_TITLES[type], data);
  let y = 112;

  if (type === 'monthly_scrap') {
    drawMonthlyScrapDashboard(doc, data, margin);
  } else if (filteredSections) {
    filteredSections.forEach((section, index) => {
      if (index > 0) {
        doc.addPage();
        createDocument(section.title, data, doc);
      }
      addTableSection(doc, section, 112, margin);
    });
  } else if (type === 'weekly') {
    const model = weeklyModel(data);
    y = drawBarChart(doc, 'Producción por día', model.days.map((day) => ({ label: day.label, value: day.production })), y, margin);
    y = addTableSection(doc, {
      title: 'Detalle por día', head: ['Fecha', 'Producción', 'Scrap', '% Scrap', 'Tiempo operativo', 'Tiempo muerto'],
      rows: model.days.map((day) => [day.label, fmt(day.production), fmt(day.scrap), pct((day.production + day.scrap) > 0 ? (day.scrap / (day.production + day.scrap)) * 100 : 0), `${fmt(day.runtime)} min`, `${fmt(day.downtime)} min`]),
    }, y, margin);
    y = drawBarChart(doc, 'Producción por máquina', model.machines.map((machine) => ({ label: machine.label, value: machine.production })), y, margin);
    addTableSection(doc, {
      title: 'Detalle por máquina', head: ['Máquina', 'Producción', 'Scrap', '% Scrap', 'Tiempo operativo', 'Tiempo muerto'],
      rows: model.machines.map((machine) => [machine.label, fmt(machine.production), fmt(machine.scrap), pct((machine.production + machine.scrap) > 0 ? (machine.scrap / (machine.production + machine.scrap)) * 100 : 0), `${fmt(machine.runtime)} min`, `${fmt(machine.downtime)} min`]),
    }, y, margin);
  } else {
    if (type === 'downtime_codes') y = drawDowntimeCodePareto(doc, data, y, margin);
    if (type === 'roll_changes') y = drawRollChangeBoxPlots(doc, data, y, margin);
    sectionsFor(type, data).forEach((section) => { y = addTableSection(doc, section, y, margin); });
  }

  addPageNumbers(doc);
  return doc;
};

const shiftDate = (dateISO, days) => {
  const [year, month, day] = String(dateISO).split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const AdditionalConversionPdfReport = ({ type, onBack }) => {
  const { currentSite } = useSite();
  const previewRef = useRef(null);
  const today = getBusinessDateISO();
  const [startDate, setStartDate] = useState(type === 'monthly_scrap' ? `${today.slice(0, 7)}-01` : type === 'weekly' ? shiftDate(today, -6) : today);
  const [endDate, setEndDate] = useState(type === 'monthly_scrap' ? `${today.slice(0, 7)}-${new Date(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 0).getDate()}` : today);
  const [workOrder, setWorkOrder] = useState('');
  const [includeOpen, setIncludeOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [reportCount, setReportCount] = useState(null);
  const hasWorkOrderFilter = ['work_orders', 'perlas'].includes(type) && Boolean(workOrder.trim());

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  const clearPreview = () => {
    setPdfUrl(null);
    setReportCount(null);
  };

  const generate = async () => {
    if (!currentSite?.id || (!hasWorkOrderFilter && (!startDate || !endDate))) {
      toast.error('Selecciona el sitio y el periodo');
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get('/conversion/reportes-pdf/datos', {
        params: {
          site_id: currentSite.id,
          include_open: includeOpen ? 1 : 0,
          ...(hasWorkOrderFilter ? { work_order: workOrder.trim() } : { start_date: startDate, end_date: endDate }),
        },
      });
      const reportData = ['work_orders', 'perlas'].includes(type) && workOrder.trim()
        ? filterWorkOrderData(data, workOrder.trim()) : data;
      const doc = buildAdditionalConversionPdf(type, reportData);
      setPdfUrl(URL.createObjectURL(doc.output('blob')));
      setReportCount(list(reportData.reports).length);
    } catch (error) {
      toast.error(error.response?.data?.message || error.response?.data?.errors?.end_date?.[0] || 'No fue posible generar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const title = REPORT_TITLES[type];
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button type="button" onClick={onBack} className="mb-2 text-sm font-bold text-blue-700 hover:text-blue-900">← Todos los reportes</button>
          <h2 className="text-2xl font-extrabold text-blue-900">{title}</h2>
        </div>
        {reportCount != null && <div className="rounded-lg bg-blue-50 px-4 py-2 text-sm font-bold text-blue-900">{reportCount} reportes fuente</div>}
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div><label className="mb-1 block text-sm font-bold text-slate-700">Desde</label><input type="date" disabled={hasWorkOrderFilter} value={startDate} onChange={(event) => { setStartDate(event.target.value); clearPreview(); }} className="rounded-lg border border-slate-300 px-3 py-2" /></div>
          <div><label className="mb-1 block text-sm font-bold text-slate-700">Hasta</label><input type="date" disabled={hasWorkOrderFilter} value={endDate} onChange={(event) => { setEndDate(event.target.value); clearPreview(); }} className="rounded-lg border border-slate-300 px-3 py-2" /></div>
          {['work_orders', 'perlas'].includes(type) && <div><label htmlFor="work-order-filter" className="mb-1 block text-sm font-bold text-slate-700">Work Order (opcional)</label><input id="work-order-filter" type="text" value={workOrder} onChange={(event) => { setWorkOrder(event.target.value); clearPreview(); }} placeholder="Número de Work Order" className="rounded-lg border border-slate-300 px-3 py-2" />{hasWorkOrderFilter && <p className="mt-1 text-xs text-slate-500">Busca en todo el histórico, sin filtrar por fechas.</p>}</div>}
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"><input type="checkbox" checked={includeOpen} onChange={(event) => { setIncludeOpen(event.target.checked); clearPreview(); }} className="h-4 w-4" />Incluir reportes abiertos</label>
          <button type="button" onClick={generate} disabled={loading} className="rounded-lg bg-blue-700 px-5 py-2 font-bold text-white shadow hover:bg-blue-800 disabled:bg-slate-400">{loading ? 'Generando…' : 'Generar vista previa'}</button>
          {pdfUrl && <><button type="button" onClick={() => previewRef.current?.contentWindow?.print()} className="rounded-lg border border-blue-300 bg-white px-5 py-2 font-bold text-blue-800">Imprimir</button><a href={pdfUrl} download={type === 'perlas' ? `Perla's Report-${hasWorkOrderFilter ? workOrder.trim() : `${startDate}-${endDate}`}.pdf` : hasWorkOrderFilter ? `${type}-${workOrder.trim()}.pdf` : `${type}-${startDate}-${endDate}.pdf`} className="rounded-lg border border-slate-300 bg-white px-5 py-2 font-bold text-slate-800 no-underline">Descargar PDF</a></>}
        </div>
      </section>

      {pdfUrl
        ? <section className="overflow-hidden rounded-2xl border border-slate-300 bg-slate-100 shadow-sm"><iframe ref={previewRef} src={pdfUrl} title={`Vista previa: ${title}`} className="h-[75vh] min-h-[620px] w-full" /></section>
        : <section className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center text-slate-500">Selecciona el periodo y genera la vista previa.</section>}
    </div>
  );
};

export default AdditionalConversionPdfReport;
